import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Shield, Users, Play } from "lucide-react";
import { toast } from "sonner";

export default function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const check = async () => {
      try {
        const r = await API.get("/admin/check");
        if (!r.data.is_admin) { setIsAdmin(false); return; }
        setIsAdmin(true);
        const u = await API.get("/admin/users");
        setUsers(u.data);
      } catch { setIsAdmin(false); }
      setLoading(false);
    };
    check();
  }, []);

  if (loading) return <div className="text-[#6B6B6B] py-12 text-center">Checking access...</div>;

  if (!isAdmin) return (
    <div className="text-center py-20">
      <Shield className="w-10 h-10 text-[#EB5757] mx-auto mb-3" />
      <h2 className="text-[20px] font-semibold text-[#37352F] mb-1">Access Denied</h2>
      <p className="text-[14px] text-[#6B6B6B]">This page is restricted to administrators.</p>
      <Button onClick={() => navigate("/")} variant="ghost" className="mt-4 text-[#2383E2]">Go to Dashboard</Button>
    </div>
  );

  return (
    <div data-testid="admin-panel" className="max-w-[1000px]">
      <div className="flex items-center gap-2 mb-1">
        <Shield className="w-5 h-5 text-[#37352F]" />
        <h1 className="text-[28px] font-bold text-[#37352F]">Admin Panel</h1>
      </div>
      <p className="text-[13px] text-[#6B6B6B] mb-8">Platform management and maintenance tools</p>

      {/* Users section */}
      <div className="mb-10">
        <div className="flex items-center gap-2 mb-3">
          <Users className="w-4 h-4 text-[#6B6B6B]" />
          <h2 className="text-[16px] font-semibold text-[#37352F]">Users</h2>
          <span className="text-[12px] text-[#6B6B6B] bg-[#E8E8E6] px-2 py-0.5 rounded-full">{users.length}</span>
        </div>
        <div className="border border-[#E0E0DE] rounded-[4px] overflow-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F7F6F3] hover:bg-[#F7F6F3] border-b border-[#E0E0DE]">
                <TableHead className="text-[11px] font-medium text-[#6B6B6B]">Name</TableHead>
                <TableHead className="text-[11px] font-medium text-[#6B6B6B]">Email</TableHead>
                <TableHead className="text-[11px] font-medium text-[#6B6B6B]">Provider</TableHead>
                <TableHead className="text-[11px] font-medium text-[#6B6B6B] text-right">Goals</TableHead>
                <TableHead className="text-[11px] font-medium text-[#6B6B6B] text-right">Projects</TableHead>
                <TableHead className="text-[11px] font-medium text-[#6B6B6B] text-right">Tasks</TableHead>
                <TableHead className="text-[11px] font-medium text-[#6B6B6B] text-right">Done</TableHead>
                <TableHead className="text-[11px] font-medium text-[#6B6B6B]">Last Active</TableHead>
                <TableHead className="text-[11px] font-medium text-[#6B6B6B]">Joined</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map(u => (
                <TableRow key={u.user_id} className="border-b border-[#E8E8E6] hover:bg-[#F7F6F3]/50">
                  <TableCell className="text-[13px] font-medium text-[#37352F]">{u.name}</TableCell>
                  <TableCell className="text-[13px] text-[#6B6B6B]">{u.email}</TableCell>
                  <TableCell><span className={"text-[11px] px-2 py-0.5 rounded-[3px] font-medium " + (u.provider === "google" ? "bg-[#D3E5EF] text-[#183B56]" : "bg-[#E8E8E8] text-[#555]")}>{u.provider || "email"}</span></TableCell>
                  <TableCell className="text-[13px] text-[#37352F] text-right">{u.goal_count}</TableCell>
                  <TableCell className="text-[13px] text-[#37352F] text-right">{u.project_count}</TableCell>
                  <TableCell className="text-[13px] text-[#37352F] text-right">{u.task_count}</TableCell>
                  <TableCell className="text-[13px] text-[#2383E2] text-right">{u.done_count}</TableCell>
                  <TableCell className="text-[12px] text-[#6B6B6B]">{u.last_activity ? new Date(u.last_activity).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}</TableCell>
                  <TableCell className="text-[12px] text-[#6B6B6B]">{u.created_at ? new Date(u.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Maintenance section */}
      <div>
        <h2 className="text-[16px] font-semibold text-[#37352F] mb-3">Maintenance</h2>
        <div className="border border-[#E0E0DE] rounded-[4px] p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-[14px] font-medium text-[#37352F]">Reset Table Configs</h3>
              <p className="text-[12px] text-[#6B6B6B] mt-0.5">Clears saved column widths and visibility for all users. They'll get fresh defaults on next load.</p>
            </div>
            <Button onClick={async () => { try { const r = await API.post("/admin/reset-table-configs"); toast.success(r.data.message); } catch { toast.error("Failed"); } }} size="sm" className="bg-[#37352F] hover:bg-[#1F1E1B] text-white text-[13px] h-8 shrink-0" data-testid="reset-configs">
              <Play className="w-3.5 h-3.5 mr-1" /> Reset
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
